import React from 'react';
import { Box, Grid, IconButton, InputAdornment, Paper, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import './../login/login.form.scss';
import { object, ref, string } from 'yup';
import InputForm from '../../commun/input-form';
import { Login, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    controlLengthMax,
    controlLengthMin,
    controlLowerCase,
    controlNumeric,
    controlSpecific,
    controlUpperCase,
} from '../../../utils/form-validators.util';
import { ResetPasswordDto } from '../../../services/dtos/reset-password.dto';
import CustomButton from '../../commun/custom-button';
import { Link } from 'react-router-dom';
import logoRegie from '../../../assets/img/LogoRegie.png';
import './resetPassword.form.scss';

/**
 * Composant de type Form le changement de mot de passe
 * @param props props
 * @constructor
 */
export default function ResetPasswordForm(props: {
    handlerSubmit: (e: ResetPasswordDto) => Promise<void>;
    loading: boolean;
    requireCurrentPassword?: boolean;
    title?: string;
}) {
    // etat de la visibilité du mot de passe
    const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
    // etat de la visibilité du nouveau mot de passe
    const [showPassword, setShowPassword] = React.useState(false);
    // etat de la visibilité de la confirmation du nouveau mot de passe
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false); // mise à jour de la visibilité du mot de passe
    // mise à jour de la visibilité du mot de passe
    const handleClickShowCurrentPassword = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };
    // mise à jour de la visibilité du nouveau mot de passe
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };
    // mise à jour de la visibilité de la confirmation du nouveau mot de passe
    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    //Message pour required
    const requiredMessage = 'Le champ doit-être renseigné.';
    // Nombre
    const eight = 8;
    const thirty = 30;

    // Définition des règles de validation
    const validationSchema = object({
        currentPassword: props.requireCurrentPassword ? string().required(requiredMessage) : string(),
        newPassword: string()
            .required(requiredMessage)
            .test('upper-case', 'Le mot de passe doit contenir au moins une lettre en majuscules', value =>
                controlUpperCase(value)
            )
            .test('lower-case', 'Le mot de passe doit contenir au moins une lettre en minuscules', value =>
                controlLowerCase(value)
            )
            .test('number', 'Le mot de passe doit contenir au moins un chiffre', value => controlNumeric(value))
            .test('symbole', 'Le mot de passe doit contenir au moins un caractère spécial : !@#%&', value =>
                controlSpecific(value)
            )
            .test('lengthMin', `Le mot de passe doit contenir au moins ${eight} caractères`, value =>
                controlLengthMin(value, eight)
            )
            .test('lengthMax', `Le mot de passe avoir au maximum ${thirty} caractères`, value =>
                controlLengthMax(value, thirty)
            ),
        confirmPassword: string()
            .required(requiredMessage)
            .oneOf([ref('newPassword')], 'Les mots de passe ne correspondent pas'),
    });

    //Init du formulaire
    const initValues = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    };

    // Création du formulaire
    return (
        <Box className="box-login">
            <Link to="/" className="login-brand" aria-label="Accueil">
                <img className="login-brand-logo" src={logoRegie} alt="Logo SDDEA" />
            </Link>

            <Grid container className="grid-login" spacing={2}>
                <Grid item xs={0} sm={4}></Grid>
                <Grid item xs={12} sm={4}>
                    {/*// Encapsulation dans un Container*/}
                    <Paper className="paper-login">
                        <Typography variant="h2">{props.title ?? 'Changer mon mot de passe'}</Typography>
                        {/*Formulaire*/}
                        <Formik
                            initialValues={initValues}
                            onSubmit={props.handlerSubmit}
                            validationSchema={validationSchema}
                        >
                            {({ values }) => {
                                const password = values.newPassword ?? '';
                                const requirements = [
                                    {
                                        label: 'Doit contenir 1 lettre majuscule.',
                                        valid: controlUpperCase(password),
                                    },
                                    {
                                        label: 'Doit contenir 1 lettre minuscule.',
                                        valid: controlLowerCase(password),
                                    },
                                    {
                                        label: 'Doit contenir 1 chiffre.',
                                        valid: controlNumeric(password),
                                    },
                                    {
                                        label: 'Doit contenir 1 caractère spécial.',
                                        valid: controlSpecific(password),
                                    },
                                    {
                                        label: 'Doit faire 8 caractères minimum.',
                                        valid: controlLengthMin(password, eight),
                                    },
                                ];

                                return (
                                    <Form>
                                        {props.requireCurrentPassword ? (
                                            <InputForm
                                                id="currentPassword"
                                                name="currentPassword"
                                                label="Mot de passe actuel"
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                autoComplete="on"
                                                required
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                data-testid="eye-current-password"
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickShowCurrentPassword}
                                                                edge="end"
                                                            >
                                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : null}
                                        <InputForm
                                            id="newPassword"
                                            name="newPassword"
                                            label="Mot de passe"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="on"
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            data-testid="eye-newPassword"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Box className="password-requirements" data-testid="password-requirements">
                                            <Typography className="password-requirements-title">
                                                Le mot de passe doit contenir au moins :
                                            </Typography>
                                            <ul>
                                                {requirements.map(requirement => (
                                                    <li
                                                        key={requirement.label}
                                                        className={requirement.valid ? 'valid' : 'invalid'}
                                                    >
                                                        {requirement.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                        <InputForm
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            label="Confirmation de mot de passe"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="on"
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            data-testid="eye-confirmPassword"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowConfirmPassword}
                                                            edge="end"
                                                        >
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <CustomButton
                                            label={'Valider'}
                                            type="submit"
                                            variant="contained"
                                            data-testid={'submit'}
                                            disabled={props.loading}
                                            element={<Login sx={{ marginLeft: '10px' }} />}
                                        />
                                    </Form>
                                );
                            }}
                        </Formik>
                    </Paper>
                </Grid>
                <Grid item xs={0} sm={4}></Grid>
            </Grid>
        </Box>
    );
}
